import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http'
import { Attribute } from '../../../models/attribute'
import { UrlCollection } from '../../../url-collection'
import { EntityConfiguration } from '../models/entity-configuration'
import { Observable, of } from 'rxjs'
import { map, switchMap, take, tap } from 'rxjs/operators'
import { EntityData, Entity } from '../models/entity-data'
import { GroupConfig } from '../models/group'
import { GroupMemberEntities } from '../../../models/group-member-entities'
import { HttpOptionsFactory } from '../../../models/http/http-options-factory'
import { Field } from '../models/field'
import DateTimeUtils from '../../../utils/date-time.utils'
import { AdditionalQualifier } from '../models/table-state.model'
import { EntityCount } from '../../../models/entity-count'
import { EntityReference } from '../models/entity-reference'
import { SoftFilter } from '../models/softfilter'
import { AuthService } from '../../../auth/services/auth.service'
import CryptUtils from '../../../auth/utils/crypt.utils'

@Injectable({
  providedIn: 'root'
})
export class EntityService {
  constructor(
    private http: HttpClient,
    private auth: AuthService
  ) {}

  // Entities
  getEntityConfigurations(): Observable<Record<string, EntityConfiguration>> {
    return this.http
      .get<
        Record<string, EntityConfiguration>
      >(UrlCollection.Entities.CONFIGS(), new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build())
      .pipe(
        map((configObj) => {
          const keys = Object.keys(configObj)
          keys.forEach((key) => {
            const obj = Object.assign(new EntityConfiguration(), configObj[key])
            configObj[key] = obj
          })
          return configObj
        })
      )
  }

  getEntityConfigurationFile(configName: string): Observable<Object> {
    return this.http.get(
      UrlCollection.Entities.CONFIG_FILE(configName),
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  updateEntityConfigurationFile(configName: string, config: string) {
    return this.http.post(
      UrlCollection.Entities.CONFIG_FILE(configName),
      { content: config },
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  getEntity(fields: Field[], type: string, id: number): Observable<Entity> {
    return this.http
      .get<Entity>(
        UrlCollection.Entities.GET(type, id),
        new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
      )
      .pipe(map((entity) => this.convertDateFieldsToDate(fields, entity)))
  }

  getEntityByHrid(fields: Field[], type: string, hrid: string): Observable<Entity> {
    return this.http
      .get<Entity>(
        UrlCollection.Entities.GET_BY_HRID(type, hrid),
        new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
      )
      .pipe(map((entity) => this.convertDateFieldsToDate(fields, entity)))
  }

  filter(
    fields: Field[],
    type: string,
    page: number,
    pageSize: number,
    mainId: number,
    qualifier: string,
    sorting: string,
    additionalQualifier?: AdditionalQualifier,
    ownerType?: string,
    ownerId?: number
  ): Observable<EntityData> {
    const sign = CryptUtils.buildHash(
      `${qualifier ?? ''}${additionalQualifier?.filter ?? ''}${sorting ?? ''}`,
      this.auth.localUser.token
    )
    return this.http
      .post<EntityData>(
        UrlCollection.Entities.FILTER(),
        { type, page, pageSize, mainId, ownerType, ownerId, qualifier, additionalQualifier, sorting, sign },
        new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
      )
      .pipe(map((entityData) => this.convertEntityDataDateFieldsToDate(fields, entityData)))
  }

  /**
   * TODO: We need a cleanup here regarding the "${<expression>}" this has been made because of default values which often need not to be evaluated by BE -
   * TODO: but we should not be forced to wrap ${ } around any condition which needs to be evaluated by BE anyway
   */
  eval(
    expression: string,
    resultFieldType?: string,
    entityReference?: EntityReference,
    context?: Map<String, any>,
    mainId?: number
  ): Observable<any> {
    const payload = { expression: expression, resultType: resultFieldType }
    if (entityReference) {
      payload['entityReference'] = entityReference
    }
    if (context) {
      payload['context'] = context
    }
    if (mainId) {
      expression = expression.replace(/\$\{ENTITY.mainId\}|ENTITY.mainId/, `${mainId}`)
    }

    // The evaluation of an expression containing ENTITY does not make sense in case of an undefined entity (e.g. creation mode)
    // TODO: this should be Backend logic...
    if (!entityReference && expression?.indexOf('ENTITY') > -1) {
      return of({ result: '' })
    }
    if (expression.includes('${')) {
      payload['sign'] = CryptUtils.buildHash(expression, this.auth.localUser.token)
      return this.http
        .post(
          UrlCollection.Actions.EVAL(),
          payload,
          new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
        )
        .pipe(
          // tap(_ => console.log),
          map((response) => {
            if (resultFieldType === 'Date' || resultFieldType === 'DateTime') {
              response['result'] = DateTimeUtils.convertApiDateTimeStringToDate(response['result'])
            }
            return response
          })
        )
    } else {
      return of({ result: expression })
    }
  }

  evalSoftFilter$(softFilter: SoftFilter): Observable<any> {
    const payload = softFilter

    return this.http.post(
      UrlCollection.EVAL_SOFT_FILTER(),
      payload,
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  exportTable(
    type: String,
    page: Number,
    pageSize = -1,
    mainId: number,
    qualifier: String,
    sorting: String,
    fileType: string
  ): Observable<HttpResponse<Blob>> {
    const url = UrlCollection.Entities.EXPORT(fileType)
    const body = { type: type, page: page, pageSize: pageSize, mainId: mainId, qualifier: qualifier, sorting: sorting }

    return this.http.post(url, body, {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      responseType: 'blob', // 'arraybuffer' | 'blob' | 'json' | 'text'; default is 'json'
      observe: 'response'
    })

    // preferably this way. but it just does not work. :-()
    // return this.http.post(url, body,
    //   new HttpOptionsFactory()
    //     .addContentTypeJson()
    //     .addResponseType('blob')
    //     .addObserveOption()
    //     .buildNew()
    // );
  }

  getEntityDataFromUrl(path: string): Observable<EntityData> {
    return this.http.get<EntityData>(
      UrlCollection.API_BASE_PATH() + path,
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  postEntityDataFromUrl(path: string, data: any): Observable<EntityData> {
    return this.http.post<EntityData>(
      UrlCollection.API_BASE_PATH() + path,
      data,
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  createEntity(fields: Field[], type: String, data): Observable<Entity> {
    data = this.convertDateToDateFields(fields, data)
    return this.http
      .post<Entity>(
        UrlCollection.Entities.CREATE(),
        { type: type, fields: data },
        new HttpOptionsFactory().addContentTypeJson().build()
      )
      .pipe(map((entity) => this.convertDateFieldsToDate(fields, entity)))
  }

  countEntities$(dtoType: string, filterExpression: string): Observable<EntityCount> {
    return this.eval(filterExpression).pipe(
      switchMap((response) => {
        const sign = CryptUtils.buildHash(`${response.result ?? ''}`, this.auth.localUser.token)
        return this.http.post<EntityCount>(
          UrlCollection.Entities.COUNT(),
          { type: dtoType, qualifier: response.result, sign },
          new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
        )
      })
    )
  }

  updateEntity(fields: Field[], type: String, id: number, data, _lastUpdate_?: Date): Observable<Entity> {
    data = this.convertDateToDateFields(fields, data)
    const payload = { type: type, fields: { ...data, ...{ id: id } } }
    if (_lastUpdate_) {
      // lastUpdate will not be sent for inline edit. Otherwwise lastUpdate will be sent.
      payload.fields['_lastUpdate_'] = _lastUpdate_
    }

    return this.http
      .post<Entity>(
        UrlCollection.Entities.UPDATE(),
        payload,
        new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
      )
      .pipe(map((entity) => this.convertDateFieldsToDate(fields, entity)))
  }

  updateEntityV2(type: string, id: number, data, _lastUpdate_: string): Observable<Entity> {
    const payload = { type: type, fields: { ...data, ...{ id: id, _lastUpdate_: _lastUpdate_ } } }

    return this.http.post<Entity>(
      UrlCollection.Entities.UPDATE(),
      payload,
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  deleteEntity(type: String, id: Number) {
    return this.http.post(
      UrlCollection.Entities.DELETE(),
      { type: type, fields: { id: id } },
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  getTemplateFields$(templateId: number, dtoType: string): Observable<Field[]> {
    return this.http.get<Field[]>(
      UrlCollection.Entities.GET_TEMPLATE_FIELDS(templateId, dtoType),
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  // Groups
  getGroupConfigurations(): Observable<Record<string, GroupConfig>> {
    return this.http.get<Record<string, GroupConfig>>(UrlCollection.Groups.CONFIGS())
  }

  getGroupMembers(type: String, holderId: Number): Observable<GroupMemberEntities> {
    return this.http.post<GroupMemberEntities>(
      UrlCollection.Groups.MEMBERS(),
      { type: type, holderId: holderId },
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  getGroupMemberIds(type: String, holderId: Number): Observable<number[]> {
    return this.http.post<number[]>(
      UrlCollection.Groups.MEMBERIDS(),
      { type: type, holderId: holderId },
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  addMember(type: String, holderId: Number, memberId: Number, position: number): Observable<Boolean> {
    return this.http.post<Boolean>(
      UrlCollection.Groups.ADDMEMBER(),
      { type: type, holderId: holderId, memberId: memberId, position: position },
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  addMembers(type: String, holderId: number, memberIds: number[], positions: number[]): Observable<Boolean> {
    return this.http.post<Boolean>(
      UrlCollection.Groups.ADDMEMBERS(),
      { type: type, holderId: holderId, memberIds: memberIds, positions: positions },
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  removeMember(type: String, holderId: Number, memberId: Number): Observable<Boolean> {
    return this.http.post<Boolean>(
      UrlCollection.Groups.REMOVEMEMBER(),
      { type: type, holderId: holderId, memberId: memberId },
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  removeMembers(type: String, holderId: number, memberIds: number[]): Observable<Boolean> {
    return this.http.post<Boolean>(
      UrlCollection.Groups.REMOVEMEMBERS(),
      { type: type, holderId: holderId, memberIds: memberIds },
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  reorderMembers(type: String, holderId: Number, memberIds: number[]): Observable<Boolean> {
    return this.http.post<Boolean>(
      UrlCollection.Groups.REORDERMEMBERS(),
      { type: type, holderId: holderId, memberIds: memberIds },
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  // Entity Attachments
  getAttachments(attachmentType: string, type: string, id: number) {
    return this.http.get(
      UrlCollection.EntityAttachments.ENTRIES(attachmentType, type, id),
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  getAttributes(type: string, id: number, attrGroup?: string): Observable<Attribute[]> {
    return this.http
      .get<
        Attribute[]
      >(UrlCollection.EntityAttributes.ENTRIES(type, id), new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build())
      .pipe(map((attributes) => attributes.map((attribute) => this.convertAttributeDateFieldtoDate(attribute))))
  }

  removeAttachmentEntry(attachmentType: string, id: number) {
    return this.http.post(
      UrlCollection.EntityAttachments.REMOVE(attachmentType),
      { id: id },
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  updateAttachmentEntry(attachmentType: string, attachmentEntry: any) {
    return this.http.post(
      UrlCollection.EntityAttachments.UPDATE(attachmentType),
      attachmentEntry,
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  addAttachmentEntry(attachmentType: string, attachmentEntry: any) {
    return this.http.post(
      UrlCollection.EntityAttachments.ADD(attachmentType),
      attachmentEntry,
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  updateAttribute(attribute: Attribute): Observable<Attribute> {
    return this.http
      .post<Attribute>(
        UrlCollection.EntityAttributes.UPDATE(),
        attribute,
        new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
      )
      .pipe(map((updAttr) => this.convertAttributeDateFieldtoDate(updAttr)))
  }

  addAttribute(attribute: Attribute): Observable<Attribute> {
    return this.http
      .post<Attribute>(
        UrlCollection.EntityAttributes.ADD(),
        attribute,
        new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
      )
      .pipe(map((newAttribute) => this.convertAttributeDateFieldtoDate(newAttribute)))
  }

  removeAttribute(id: number) {
    return this.http.post(
      UrlCollection.EntityAttributes.REMOVE(),
      { id: id },
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  // fields of type Date or DateTime need to be converted to proper Date for the GUI
  // in: Entity; out: Entity with GUI conform date format
  convertDateFieldsToDate(fields: Field[], entity: Entity): Entity {
    fields
      .filter((field) => field.type === 'Date' || field.type === 'DateTime' || field.type === 'DatePeriod')
      .forEach((field) => {
        if (field.type === 'DatePeriod') {
          entity.fields[field.field] = [
            DateTimeUtils.convertApiDateTimeStringToDate(entity.fields[field.field]['start']),
            DateTimeUtils.convertApiDateTimeStringToDate(entity.fields[field.field]['end'])
          ]
          if (!entity.fields[field.field][0] && !entity.fields[field.field][1]) entity.fields[field.field] = null
        } else {
          entity.fields[field.field] = DateTimeUtils.convertApiDateTimeStringToDate(entity.fields[field.field])
        }
      })
    return entity
  }

  // fields of type Date or DateTime need to be converted to proper Date for the GUI
  // in: Entity with GUI conform date format out: Entity;
  convertDateToDateFields(fields: Field[], entity: Entity): Entity {
    fields
      .filter((field) => field.type === 'Date' || field.type === 'DateTime' || field.type === 'DatePeriod')
      .forEach((field) => {
        if (entity[field.field]) {
          if (field.type === 'DatePeriod') {
            entity[field.field] = {
              start: DateTimeUtils.convertDateToApiConformTimeString(entity[field.field][0]),
              end: DateTimeUtils.convertDateToApiConformTimeString(entity[field.field][1])
            }
          } else {
            entity[field.field] = DateTimeUtils.convertDateToApiConformTimeString(entity[field.field])
          }
        }
      })
    return entity
  }

  // fields of type Date or DateTime need to be converted to proper Date for the GUI
  // in: EntityData; out: EntityData with GUI conform date format
  convertEntityDataDateFieldsToDate(fields: Field[], entityData: EntityData): EntityData {
    if (fields) {
      entityData.data.forEach((entity) => {
        fields
          .filter((field) => field.type === 'Date' || field.type === 'DateTime' || field.type === 'DatePeriod')
          .forEach((field) => {
            if (field.type === 'DatePeriod') {
              entity[field.field] = [
                DateTimeUtils.convertApiDateTimeStringToDate(entity[field.field]['start']),
                DateTimeUtils.convertApiDateTimeStringToDate(entity[field.field]['end'])
              ]
              if (!entity[field.field][0] && !entity[field.field][1]) entity[field.field] = null
            } else {
              entity[field.field] = DateTimeUtils.convertApiDateTimeStringToDate(entity[field.field])
            }
          })
      })
    }
    return entityData
  }

  convertAttributeDateFieldtoDate(attribute: Attribute): Attribute {
    if (attribute.attributeType === 'Date' || attribute.attributeType === 'DateTime') {
      attribute.dateValue = DateTimeUtils.convertApiDateTimeStringToDate(attribute.value)
    }
    return attribute
  }
}
